/**
 * Multi-tenant hooks: list the user's tenants, switch between them, create
 * new workspaces. All keyed under `['me', ...]` so the set survives a
 * tenant switch (every `['tenant', tenantId, ...]` cache gets evicted).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { tokenManager } from '../lib/tokenManager'

export type Membership = {
  tenantId: string
  tenantName: string
  tenantSlug?: string
  tenantPlan?: string
  roleName: string
  membershipType?: string
  isOwner: boolean
}

export type MembershipsResponse = { memberships: Membership[] }

// Keys that leak per-tenant settings across switches when they linger in
// localStorage (Company page stored them globally pre-multi-tenant).
const TENANT_SCOPED_LOCALSTORAGE_KEYS = [
  'userCountry',
  'userTimezone',
  'userFiscalStartMonth',
]

function clearTenantScopedLocalStorage(): void {
  try {
    TENANT_SCOPED_LOCALSTORAGE_KEYS.forEach((k) => localStorage.removeItem(k))
  } catch {
    /* storage unavailable */
  }
}

export function useMemberships(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled !== false
  return useQuery({
    // NOT tenant-keyed: survives every switch. That's the whole point.
    queryKey: ['me', 'memberships'] as const,
    queryFn: async () => {
      const res = await api.get<MembershipsResponse>('/api/auth/memberships')
      return res.memberships ?? []
    },
    staleTime: 5 * 60 * 1000,
    // Callers in pending-session mode (no App JWT yet) must pass
    // enabled: false - otherwise the query hits /api/auth/memberships,
    // 401s, and the api.ts redirect-on-401 dance kicks the user to /login
    // before they can pick a workspace.
    enabled,
  })
}

type SwitchResponse = {
  token: string
  tenantId: string
  tenantName: string
  role: string
  permissions: string[]
}

export function useSwitchTenant() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async (tenantId: string) => {
      return await api.post<SwitchResponse>('/api/auth/switch-tenant', { tenantId })
    },
    onSuccess: (res) => {
      // Order matters: set the new token BEFORE evicting tenant caches so
      // any in-flight refetch uses the new Authorization header + tenantId.
      tokenManager.set(res.token)
      qc.removeQueries({ queryKey: ['tenant'] })
      clearTenantScopedLocalStorage()
      // Land on the dashboard - permissions may have shifted and the
      // current URL could be something the new role can't see.
      navigate('/dashboard', { replace: true })
    },
  })
}

type CreateWorkspaceResponse = { membership: Membership }

export function useCreateWorkspace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      return await api.post<CreateWorkspaceResponse>('/api/auth/create-workspace', { name })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'memberships'] })
    },
  })
}

type SelectTenantResponse = {
  token: string
  userId: number | string
  email: string
  name: string | null
  tenantId: string
  tenantName: string
  role: string
  permissions: string[]
}

/**
 * Exchange a pending_session token + chosen tenantId for a real App JWT.
 * Used only by WorkspacePickerPage in its post-login mode (when the user
 * arrived from /login or OAuth callback with 2+ memberships).
 */
export function useSelectTenant() {
  return useMutation({
    mutationFn: async ({ pendingSessionToken, tenantId }: { pendingSessionToken: string; tenantId: string }) => {
      return await api.post<SelectTenantResponse>('/api/auth/select-tenant', {
        pendingSessionToken,
        tenantId,
      })
    },
  })
}

/**
 * Change-password flow (logged-in user, email verification):
 *   1. POST /change-password (no body) → triggers Cognito code email.
 *   2. POST /confirm-change-password { code, newPassword } → rotates pwd.
 */
export function useRequestPasswordChange() {
  return useMutation({
    mutationFn: async () => {
      return await api.post<{ ok: true; message: string }>('/api/auth/change-password')
    },
  })
}

export function useConfirmPasswordChange() {
  return useMutation({
    mutationFn: async ({ code, newPassword }: { code: string; newPassword: string }) => {
      return await api.post<{ ok: true }>('/api/auth/confirm-change-password', {
        code,
        newPassword,
      })
    },
  })
}

/**
 * Danger-zone step 1: ask the backend to email a 6-digit confirmation
 * code to the caller's address. The code is stored on the `tenants` row
 * and expires in 10 minutes. Caller must already have workspace.delete
 * - the route returns 403 otherwise so this can't be used to probe
 * other workspaces' inboxes.
 */
export function useRequestDeleteWorkspaceCode() {
  return useMutation({
    mutationFn: async () => {
      return api.post<{ message: string; email: string }>(
        '/api/tenant/workspace/request-delete-code',
        {},
      )
    },
  })
}

/**
 * Danger-zone step 2: delete the CURRENT tenant. Requires the typed
 * workspace name + the 6-digit code mailed in step 1. Server soft-
 * deletes the row and revokes every refresh token for the tenant, so
 * callers should clear their local state and redirect to /login
 * immediately on success.
 */
export function useDeleteWorkspace() {
  return useMutation({
    mutationFn: async (vars: { name: string; verificationCode: string }) => {
      // The fetch wrapper's `del` doesn't take a body in some
      // environments; round-trip through fetch directly so we can ship
      // the name + code on the request body.
      await api.del<void>('/api/tenant/workspace', {
        body: { name: vars.name, verificationCode: vars.verificationCode },
      })
    },
  })
}
